/* Vaultiqe — "Most relevant" sort (collection merchandising)
 *
 * Reorders the children of any container marked with
 * `data-vq-display-priority-sort="active"` — only the "Most relevant" (manual)
 * sort opts in; every other sort is left literal.
 *
 * The order is built to maximise conversion: surface a varied, high-intent
 * first screen rather than a wall of one model, while still honouring the
 * merchandiser's per-product display priority.
 *
 * ── Philosophy ────────────────────────────────────────────────────────────
 *  Diversity is the PRIMARY axis: the first row must show four *different* bag
 *  models, never three of the same. Display priority is folded in as an
 *  ordering signal WITHIN that diverse structure — it decides which model
 *  family leads, and which exact piece of a model surfaces first — but it no
 *  longer clusters several same-model pieces at the top.
 *
 * ── How the order is built ────────────────────────────────────────────────
 *  1. Availability        — in-stock pieces always before sold ones.
 *  2. Within-bucket order — each piece is pre-ranked by display priority
 *                           (top > high > medium > low; blank → medium), then
 *                           better condition, then newer.
 *  3. Diversity (global, the whole availability group at once):
 *       • Single-house grid (e.g. /collections/louis-vuitton)
 *           → round-robin by MODEL FAMILY (Pochette, Speedy, Keepall, Neverfull…),
 *             so "Pochette Accessoires / Métis / Félicie" count as ONE family
 *             and only one of them can appear in any given round (= row).
 *             Within a family, sub-models are themselves spread.
 *       • Multi-house grid (e.g. /collections/all)
 *           → round-robin by DESIGNER (LV, Hermès, Dior…), each designer's
 *             pieces spread by family inside.
 *     Families/designers are ordered by their strongest piece's display
 *     priority, then by how many pieces they have (bigger leads), so round one
 *     — the first visible row — is one card from each of the most relevant
 *     families: maximum model variety up top.
 *
 * "Multi-house" is only triggered when at least two designers each have ≥2
 * pieces — so a single mislabelled/odd-vendor item can't flip a single-house
 * page into designer mode.
 *
 * Cards opt in with `data-vq-card` and carry: `data-display-priority`,
 * `data-model`, `data-designer`, `data-condition-rank`, `data-created-at`,
 * and either `data-available="true|false"` or the `cb__card--sold` class.
 *
 * Idempotent; exposed as `window.vqApplyDisplayPrioritySort`.
 */
(function () {
  var RANK = { top: 4, high: 3, medium: 2, low: 1 };
  var DEFAULT_RANK = RANK.medium; /* blank display_priority → medium */

  function availabilityScore(card) {
    var attr = card.getAttribute('data-available');
    if (attr === 'true')  return 1;
    if (attr === 'false') return 0;
    if (card.classList.contains('cb__card--sold')) return 0;
    if (card.classList.contains('grid__item--sold-out')) return 0;
    return 1;
  }
  function priorityScore(card) {
    var v = (card.getAttribute('data-display-priority') || '').toLowerCase();
    return RANK[v] || DEFAULT_RANK;
  }
  function conditionScore(card) {
    var n = parseInt(card.getAttribute('data-condition-rank'), 10);
    return isNaN(n) ? 0 : n;
  }
  function modelKey(card) { return (card.getAttribute('data-model') || '').trim(); }
  /* Model FAMILY — the first token of the model key. "pochette-accessoires",
     "pochette-metis" and "pochette-felicie" all collapse to "pochette", so the
     diversifier treats every Pochette variant as one model for the first row.
     "keepall-45"/"keepall-55" → "keepall", "neverfull-mm" → "neverfull", etc. */
  function familyKey(card) {
    var m = modelKey(card);
    if (!m) return ' ';
    var i = m.indexOf('-');
    return i === -1 ? m : m.slice(0, i);
  }
  function designerKey(card) {
    return (card.getAttribute('data-designer') ||
            card.getAttribute('data-cb-designer-slug') || '').trim();
  }

  /* Split cards into buckets keyed by keyFn, in first-appearance order. Blank
     keys collapse into one bucket. Returns { order:[key…], map:{key:[card…]} }. */
  function bucketize(cards, keyFn) {
    var order = [], map = {};
    cards.forEach(function (c) {
      var k = keyFn(c) || ' ';
      if (!map[k]) { map[k] = []; order.push(k); }
      map[k].push(c);
    });
    return { order: order, map: map };
  }

  /* Round-robin an ordered list of buckets: one from each per round, preserving
     each bucket's internal order. */
  function roundRobin(buckets) {
    var out = [], added = true;
    for (var round = 0; added; round++) {
      added = false;
      for (var i = 0; i < buckets.length; i++) {
        if (round < buckets[i].length) { out.push(buckets[i][round]); added = true; }
      }
    }
    return out;
  }

  /* Round-robin cards bucketed by keyFn, in first-appearance bucket order. */
  function roundRobinByKey(cards, keyFn) {
    var b = bucketize(cards, keyFn);
    return roundRobin(b.order.map(function (k) { return b.map[k]; }));
  }

  /* Highest display-priority found among a set of cards (used to order the
     diversity buckets so the strongest family/designer leads round one). */
  function maxPriority(cards) {
    var max = 0;
    for (var i = 0; i < cards.length; i++) {
      var p = priorityScore(cards[i]);
      if (p > max) max = p;
    }
    return max;
  }

  /* Order a bucketize() result's keys by: strongest piece first, then larger
     bucket first, then first appearance (stable). Returns the sorted key list. */
  function rankBucketOrder(bk) {
    return bk.order.slice().sort(function (ka, kc) {
      var pa = maxPriority(bk.map[ka]), pc = maxPriority(bk.map[kc]);
      if (pa !== pc) return pc - pa;
      if (bk.map[ka].length !== bk.map[kc].length) return bk.map[kc].length - bk.map[ka].length;
      return bk.order.indexOf(ka) - bk.order.indexOf(kc);
    });
  }

  /* Spread a set of cards so adjacent cards are different bag models: round-robin
     across model FAMILIES, and within each family spread its sub-models too. The
     first `N` cards (N = number of families) are therefore N different models. */
  function diversifyByFamily(cards) {
    var fam = bucketize(cards, familyKey);
    var spread = {};
    fam.order.forEach(function (f) { spread[f] = roundRobinByKey(fam.map[f], modelKey); });
    var order = rankBucketOrder(fam);
    return roundRobin(order.map(function (f) { return spread[f]; }));
  }

  /* Arrange one availability group. Single-house → diversify by model family.
     Multi-house → round-robin by designer, each designer spread by family. */
  function arrange(cards, multiDesigner) {
    if (!multiDesigner) return diversifyByFamily(cards);
    var bd = bucketize(cards, designerKey);
    var spread = {};
    bd.order.forEach(function (d) { spread[d] = diversifyByFamily(bd.map[d]); });
    var order = rankBucketOrder(bd);
    return roundRobin(order.map(function (d) { return spread[d]; }));
  }

  function sortGrid(grid) {
    var cards = Array.prototype.slice.call(grid.querySelectorAll(':scope > [data-vq-card]'));
    if (cards.length < 2) return;

    /* Within-bucket order: higher display priority first, then better
       condition, then newer — so the strongest piece of each model surfaces. */
    cards.sort(function (a, b) {
      var pa = priorityScore(a), pb = priorityScore(b);
      if (pa !== pb) return pb - pa;
      var ca = conditionScore(a), cb = conditionScore(b);
      if (ca !== cb) return cb - ca;
      var da = a.getAttribute('data-created-at') || '';
      var db = b.getAttribute('data-created-at') || '';
      if (da === db) return 0;
      return db < da ? -1 : 1;
    });

    /* Multi-house only when ≥2 designers each have ≥2 pieces — one odd-vendor
       item can't flip a single-house page into designer mode. */
    var counts = {};
    cards.forEach(function (c) { var d = designerKey(c); if (d) counts[d] = (counts[d] || 0) + 1; });
    var strongDesigners = 0;
    for (var k in counts) { if (counts[k] >= 2) strongDesigners++; }
    var multiDesigner = strongDesigners >= 2;

    /* Sold pieces always behind available ones; each set diversified on its own. */
    var avail = [], sold = [];
    cards.forEach(function (c) { (availabilityScore(c) ? avail : sold).push(c); });

    var ordered = arrange(avail, multiDesigner).concat(arrange(sold, multiDesigner));
    ordered.forEach(function (c) { grid.appendChild(c); });
    placeInterstitials(grid, ordered);
    promoteLeadImages(ordered);
  }

  /* ── Blocks that belong BETWEEN rows ──────────────────────────────────────
     The grid can contain children that are not cards — the transparency exhibit
     and the card-stack slot on the collection page. They carry
     data-cb-after-card="N" and are meant to sit after the Nth card.

     They have to be re-placed HERE, and this is not defensive coding: the loop
     above re-appends every card to the end of the grid, which silently pushes
     everything that is not a card to the front of it. Positioning them in Liquid
     is therefore not enough — the server order is exactly what this file rewrites,
     and both blocks ended up stacked above the first listing.

     Placement is by the card's element, not by an index into the live DOM, so it
     does not matter how many blocks are inserted or in what order. */
  function placeInterstitials(grid, ordered) {
    var blocks = Array.prototype.slice.call(grid.querySelectorAll(':scope > [data-cb-after-card]'));
    if (!blocks.length) return;
    blocks.forEach(function (b) {
      var n = parseInt(b.getAttribute('data-cb-after-card'), 10);
      if (!(n > 0)) return;
      var anchor = ordered[n];                       /* the card that should follow it */
      if (anchor && anchor.parentNode === grid) grid.insertBefore(b, anchor);
      else grid.appendChild(b);                      /* fewer cards than N: park it at the end */
    });
  }

  /* ── Lead-image priority (LCP correctness) ────────────────────────────────
     Liquid marks the first few cards eager/fetchpriority:high — but it can only
     use the SERVER order, and this file has just rewritten that order. Without
     this step the browser races to fetch images that JS then pushes far down
     the grid, while the cards now sitting on the first screen are still
     `loading="lazy"` and don't begin loading until they scroll into view. The
     result is a slow, visibly late-filling first screen — a real LCP
     regression on the default "Most relevant" sort.

     So after re-ordering we re-point the priority at whatever is genuinely on
     top now: the first LEAD_COUNT cards are forced to load, and the first
     PRIORITY_COUNT are additionally marked high priority. Images the server
     already got right are `complete` (or in flight) and skipped, so this
     costs nothing in the common case and never double-fetches. */
  var LEAD_COUNT = 6;      /* ≈ first screen: 3 rows on mobile (2-up), 1.5 on desktop (4-up) */
  var PRIORITY_COUNT = 2;  /* the true LCP candidates */

  function promoteLeadImages(ordered) {
    for (var n = 0; n < ordered.length && n < LEAD_COUNT; n++) {
      var img = ordered[n].querySelector('img');
      /* `complete` covers both "already loaded" and "errored" — nothing to do. */
      if (!img || img.complete) continue;

      if (n < PRIORITY_COUNT) {
        try { img.setAttribute('fetchpriority', 'high'); } catch (e) {}
      }
      if (img.loading === 'lazy') {
        img.loading = 'eager';
        /* Re-assert the source: some engines don't re-evaluate a lazy image on
           the attribute flip alone. Assigning the same value is a no-op once a
           fetch has started, so this cannot trigger a second download. */
        var ss = img.getAttribute('srcset');
        if (ss) img.setAttribute('srcset', ss);
        var src = img.getAttribute('src');
        if (src) img.setAttribute('src', src);
      }
    }
  }

  function applyAll(root) {
    (root || document).querySelectorAll('[data-vq-display-priority-sort="active"]').forEach(sortGrid);
  }

  window.vqApplyDisplayPrioritySort = applyAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { applyAll(); });
  } else {
    applyAll();
  }
})();
